import { PermissionType, UserRole } from '@shared/models';

import { AdminModules, CompanyModules, ProjectModules } from './app-modules.consts';

export type PermissionsList = Record<AdminModules | CompanyModules | ProjectModules, PermissionType[]>;

const allPermissions = [PermissionType.view, PermissionType.create, PermissionType.edit, PermissionType.delete];
const viewPermission = [PermissionType.view];

export function getPermissions(role: UserRole): PermissionsList | undefined {
  switch (role) {
    case UserRole.ProjectManager:
      return projectManagerPermissions;

    case UserRole.Foreman:
      return foremanPermissions;

    case UserRole.Worker:
      return workerPermissions;

    case UserRole.Agent:
      return agentPermissions;

    case UserRole.LearningManager:
      return learningManagerPermissions;

    case UserRole.HR:
      return hrPermissions;

    case UserRole.Stakeholder:
      return stakeholderPermissions;
  }

  return undefined;
}

export const projectManagerPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: allPermissions,
  company_projects: allPermissions,
  company_employeeHours: allPermissions,
  company_timeAndCostCategories: allPermissions,
  company_staffing: allPermissions,
  company_materials: allPermissions,
  company_materialPackages: allPermissions,
  company_customers: allPermissions,
  company_qaCheckItems: allPermissions,
  company_subscription: allPermissions,
  company_users: allPermissions,
  company_dataAudit: allPermissions,
  company_salesData: allPermissions,
  company_employeeHoursPeriodLock: viewPermission,
  company_serviceTimeTracker: allPermissions,
  company_templatesAndDefaults: allPermissions,
  report_employeeHours: allPermissions,

  project_setup: allPermissions,
  project_dashboard: allPermissions,
  project_materialAssignment: allPermissions,
  project_costEstimate: allPermissions,
  project_timeTracker: allPermissions,
  project_projectPlanning: allPermissions,
  project_progress: allPermissions,
  project_employeeAssignment: allPermissions,
  project_employeeHours: allPermissions,
  project_constructionAreas: allPermissions,
  project_activities: allPermissions,
  project_phases: allPermissions,
  project_priceFactorsAndLabourCosts: allPermissions,
  project_qaCheckMatrix: allPermissions,
  project_workingHours: allPermissions,
  project_dataAudit: allPermissions,
  project_preferences: allPermissions,
  project_files: allPermissions,
  project_plans: allPermissions,
  project_master_project: allPermissions,
  project_sub_projects: allPermissions,
  company_workTasks: allPermissions,
  project_workTasks: allPermissions,
};

export const foremanPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: viewPermission,
  company_projects: viewPermission,
  company_employeeHours: [],
  company_timeAndCostCategories: [],
  company_staffing: allPermissions,
  company_materials: [],
  company_materialPackages: [],
  company_customers: [],
  company_qaCheckItems: [],
  company_subscription: [],
  company_users: [],
  company_dataAudit: [],
  company_salesData: [],
  company_employeeHoursPeriodLock: [],
  company_serviceTimeTracker: allPermissions,
  company_templatesAndDefaults: [],
  report_employeeHours: viewPermission,

  project_setup: [],
  project_dashboard: viewPermission,
  project_materialAssignment: [],
  project_costEstimate: [],
  project_timeTracker: [],
  project_projectPlanning: [],
  project_progress: allPermissions,
  project_employeeAssignment: allPermissions,
  project_employeeHours: allPermissions,
  project_constructionAreas: viewPermission,
  project_activities: viewPermission,
  project_phases: viewPermission,
  project_priceFactorsAndLabourCosts: [],
  project_qaCheckMatrix: viewPermission,
  project_workingHours: viewPermission,
  project_dataAudit: [],
  project_preferences: [],
  project_files: viewPermission,
  project_plans: viewPermission,

  project_master_project: [],
  project_sub_projects: [],
  company_workTasks: allPermissions,
  project_workTasks: allPermissions,
};

export const workerPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: [],
  company_projects: [],
  company_employeeHours: [],
  company_timeAndCostCategories: [],
  company_staffing: [],
  company_materials: [],
  company_materialPackages: [],
  company_customers: [],
  company_qaCheckItems: [],
  company_subscription: [],
  company_users: [],
  company_dataAudit: [],
  company_salesData: [],
  company_employeeHoursPeriodLock: [],
  company_serviceTimeTracker: [],
  company_templatesAndDefaults: [],
  report_employeeHours: [],

  project_setup: [],
  project_dashboard: [],
  project_materialAssignment: [],
  project_costEstimate: [],
  project_timeTracker: [],
  project_projectPlanning: [],
  project_progress: [],
  project_employeeAssignment: [],
  project_employeeHours: [],
  project_constructionAreas: [],
  project_activities: [],
  project_phases: [],
  project_priceFactorsAndLabourCosts: [],
  project_qaCheckMatrix: [],
  project_workingHours: [],
  project_dataAudit: [],
  project_preferences: [],
  project_files: [],
  project_plans: [],
  project_master_project: [],
  project_sub_projects: [],
  company_workTasks: [],
  project_workTasks: [],
};

export const agentPermissions: PermissionsList = {
  admin_dashboard: allPermissions,
  admin_helpManagement: [],
  admin_companies: allPermissions,
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: allPermissions,
  company_dashboard: allPermissions,
  company_projects: allPermissions,
  company_employeeHours: allPermissions,
  company_timeAndCostCategories: allPermissions,
  company_staffing: allPermissions,
  company_materials: allPermissions,
  company_materialPackages: allPermissions,
  company_customers: allPermissions,
  company_qaCheckItems: allPermissions,
  company_subscription: allPermissions,
  company_users: allPermissions,
  company_dataAudit: allPermissions,
  company_salesData: allPermissions,
  company_employeeHoursPeriodLock: viewPermission,
  company_serviceTimeTracker: allPermissions,
  company_templatesAndDefaults: allPermissions,
  report_employeeHours: allPermissions,

  project_setup: [],
  project_dashboard: [],
  project_materialAssignment: [],
  project_costEstimate: [],
  project_timeTracker: [],
  project_projectPlanning: [],
  project_progress: [],
  project_employeeAssignment: [],
  project_employeeHours: [],
  project_constructionAreas: [],
  project_activities: [],
  project_phases: [],
  project_priceFactorsAndLabourCosts: [],
  project_qaCheckMatrix: [],
  project_workingHours: [],
  project_dataAudit: [],
  project_preferences: [],
  project_files: [],
  project_plans: [],
  project_master_project: [],
  project_sub_projects: [],
  company_workTasks: [],
  project_workTasks: [],
};

export const learningManagerPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: [],
  company_projects: [],
  company_employeeHours: [],
  company_timeAndCostCategories: [],
  company_staffing: [],
  company_materials: [],
  company_materialPackages: [],
  company_customers: [],
  company_qaCheckItems: [],
  company_subscription: [],
  company_users: [],
  company_dataAudit: [],
  company_salesData: [],
  company_employeeHoursPeriodLock: [],
  company_serviceTimeTracker: [],
  company_templatesAndDefaults: [],
  report_employeeHours: [],

  project_setup: [],
  project_dashboard: [],
  project_materialAssignment: [],
  project_costEstimate: [],
  project_timeTracker: [],
  project_projectPlanning: [],
  project_progress: [],
  project_employeeAssignment: [],
  project_employeeHours: [],
  project_constructionAreas: [],
  project_activities: [],
  project_phases: [],
  project_priceFactorsAndLabourCosts: [],
  project_qaCheckMatrix: [],
  project_workingHours: [],
  project_dataAudit: [],
  project_preferences: [],
  project_files: [],
  project_plans: [],
  project_master_project: [],
  project_sub_projects: [],
  company_workTasks: [],
  project_workTasks: [],
};

export const hrPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: viewPermission,
  company_projects: viewPermission,
  company_employeeHours: allPermissions,
  company_timeAndCostCategories: [],
  company_staffing: allPermissions,
  company_materials: [],
  company_materialPackages: [],
  company_customers: [],
  company_qaCheckItems: [],
  company_subscription: [],
  company_users: allPermissions,
  company_dataAudit: [],
  company_salesData: viewPermission,
  company_employeeHoursPeriodLock: allPermissions,
  company_serviceTimeTracker: viewPermission,
  company_templatesAndDefaults: [],
  report_employeeHours: allPermissions,

  project_setup: [],
  project_dashboard: viewPermission,
  project_materialAssignment: [],
  project_costEstimate: allPermissions,
  project_timeTracker: [],
  project_projectPlanning: [],
  project_progress: viewPermission,
  project_employeeAssignment: allPermissions,
  project_employeeHours: allPermissions,
  project_constructionAreas: [],
  project_activities: [],
  project_phases: [],
  project_priceFactorsAndLabourCosts: [],
  project_qaCheckMatrix: [],
  project_workingHours: allPermissions,
  project_dataAudit: [],
  project_preferences: [],
  project_files: [],
  project_plans: [],
  project_master_project: [],
  project_sub_projects: [],
  company_workTasks: [],
  project_workTasks: [],
};

export const stakeholderPermissions: PermissionsList = {
  admin_dashboard: [],
  admin_helpManagement: [],
  admin_companies: [],
  admin_users: [],
  admin_appLogs: [],
  admin_emailQueue: [],
  admin_registrationRequests: [],
  admin_integrations: [],
  admin_announcements: [],

  company_details: [],
  company_dashboard: viewPermission,
  company_projects: viewPermission,
  company_employeeHours: [],
  company_timeAndCostCategories: [],
  company_staffing: [],
  company_materials: [],
  company_materialPackages: [],
  company_customers: [],
  company_qaCheckItems: [],
  company_subscription: [],
  company_users: [],
  company_dataAudit: [],
  company_salesData: [],
  company_employeeHoursPeriodLock: [],
  company_serviceTimeTracker: [],
  company_templatesAndDefaults: [],
  report_employeeHours: [],

  project_setup: viewPermission,
  project_dashboard: viewPermission,
  project_materialAssignment: viewPermission,
  project_costEstimate: viewPermission,
  project_timeTracker: viewPermission,
  project_projectPlanning: viewPermission,
  project_progress: viewPermission,
  project_employeeAssignment: viewPermission,
  project_employeeHours: viewPermission,
  project_constructionAreas: viewPermission,
  project_activities: viewPermission,
  project_phases: viewPermission,
  project_priceFactorsAndLabourCosts: viewPermission,
  project_qaCheckMatrix: viewPermission,
  project_workingHours: viewPermission,
  project_dataAudit: viewPermission,
  project_preferences: viewPermission,
  project_files: viewPermission,
  project_plans: viewPermission,
  project_master_project: viewPermission,
  project_sub_projects: viewPermission,
  company_workTasks: viewPermission,
  project_workTasks: viewPermission,
};
